import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import type { TextAreaSize } from "@design-system/TextArea/TextArea.types";
import { BooleanControl, TextareaControl } from "./playgroundControls";
import styles from "./DatePickerPlayground.module.scss";

/**
 * @purpose The TextArea sandbox's props aside. The same knobs drive the live
 * stage and, once the x-ray is on, the schematic.
 */

export const textAreaSizes = [
    "md",
    "lg",
    "xl",
] as const satisfies readonly TextAreaSize[];

export interface TextAreaFeatureFlags {
    isInvalid: boolean;
    isDisabled: boolean;
    isReadOnly: boolean;
    isAutoGrow: boolean;
    isResizable: boolean;
}

export const TextAreaPlaygroundControls = ({
    size,
    onSizeChange,
    value,
    onValueChange,
    features,
    onFeaturesChange,
}: {
    size: TextAreaSize;
    onSizeChange: (size: TextAreaSize) => void;
    value: string;
    onValueChange: (value: string) => void;
    features: TextAreaFeatureFlags;
    onFeaturesChange: (features: TextAreaFeatureFlags) => void;
}) => (
    <>
        <section>
            <h2>Size</h2>
            <ToggleGroup.Root<TextAreaSize>
                value={size}
                onValueChange={(nextSize) => {
                    if (nextSize) {
                        onSizeChange(nextSize);
                    }
                }}
            >
                {textAreaSizes.map((option) => (
                    <ToggleGroup.Item key={option} value={option}>
                        {option}
                    </ToggleGroup.Item>
                ))}
            </ToggleGroup.Root>
        </section>
        <section>
            <h2>Features</h2>
            <div className={styles.booleanGrid}>
                <BooleanControl
                    label="Invalid"
                    checked={features.isInvalid}
                    onChange={(isInvalid) =>
                        onFeaturesChange({ ...features, isInvalid })
                    }
                />
                <BooleanControl
                    label="Disabled"
                    checked={features.isDisabled}
                    onChange={(isDisabled) =>
                        onFeaturesChange({ ...features, isDisabled })
                    }
                />
                <BooleanControl
                    label="Read-only"
                    checked={features.isReadOnly}
                    onChange={(isReadOnly) =>
                        onFeaturesChange({ ...features, isReadOnly })
                    }
                />
                <BooleanControl
                    label="Auto-grow"
                    checked={features.isAutoGrow}
                    onChange={(isAutoGrow) =>
                        onFeaturesChange({
                            ...features,
                            isAutoGrow,
                            isResizable: isAutoGrow
                                ? false
                                : features.isResizable,
                        })
                    }
                />
                <BooleanControl
                    label="Resizable"
                    checked={features.isResizable}
                    onChange={(isResizable) =>
                        onFeaturesChange({
                            ...features,
                            isResizable,
                            isAutoGrow: isResizable
                                ? false
                                : features.isAutoGrow,
                        })
                    }
                />
            </div>
        </section>
        <section>
            <h2>Value</h2>
            <TextareaControl
                label="Value"
                value={value}
                onChange={onValueChange}
            />
        </section>
    </>
);
